import React, { useMemo, useState } from 'react'
import {
  ActivityTimeline,
  Button,
  Skeleton,
  type ActivityItem,
} from '@firstbite/ui'
import { ScopedEmailsModal } from './ScopedEmailsModal'
import { buildEmailFromEvent } from '../NewCompanyPage/EventHistory/utils'
import { Editor } from '../Editor/Editor'
import { JsonIntoUl } from '../json-into-ul'
import { StagePill } from '../FbUI/StagePill'
import { useRecommendationActivityQuery } from './useRecommendationActivityQuery'
import {
  describeActivityEvent,
  resolveActivityScope,
} from './recommendationActivityItems'
import type { EnrichedAction } from '../../services/recommendations_contracts'
import type { EmailMessage } from '../../models/emailIntegration'

interface RecommendationActivityTabProps {
  action: EnrichedAction
}

/** Compact placeholder rows for the first load — a plain avatar-rail thread
 *  matching the fb-ui ActivityTimeline `plain`/`sm` layout. */
const ActivitySkeleton: React.FC = () => (
  <div className="space-y-4">
    {[0, 1, 2].map((i) => (
      <div key={i} className="flex gap-3">
        <Skeleton className="size-7 shrink-0 rounded-full" />
        <div className="flex-1 space-y-1.5 pt-1">
          <Skeleton className="h-3.5 w-1/2" />
          <Skeleton className="h-3 w-5/6" />
        </div>
      </div>
    ))}
  </div>
)

/**
 * Company activity feed for the recommendation panel — an **isolated** fb-ui
 * variant. It renders the fb-ui `ActivityTimeline` off its own store-free query
 * ({@link useRecommendationActivityQuery}), sharing nothing with the deal/company
 * detail pages' `EventHistory` stack (no `EventHistoryList`, no
 * `useEventHistoryFilterStore`) — so opening this panel can never read or mutate
 * their filter state. Always company-scoped (`contact_company_id`): most outreach
 * threads to the company, and recs want the full company picture the synthesis
 * pipeline saw. Rendered only when the activity pane is open, so it fetches lazily.
 */
export const RecommendationActivityTab: React.FC<
  RecommendationActivityTabProps
> = ({ action }) => {
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null)

  // Scope (deal vs company) matches the header label — same resolver.
  const { dealId, chainProxyId } = resolveActivityScope(action)
  const {
    events,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useRecommendationActivityQuery({ dealId, chainProxyId })

  const items = useMemo<ActivityItem[]>(
    () =>
      events.map((event) => {
        const d = describeActivityEvent(event)
        const item: ActivityItem = {
          id: d.id,
          timestamp: d.timestamp,
          type: d.type,
          actor: d.actor,
          summary: d.summary,
        }
        if (d.stageChangeToId != null) {
          // Colored stage pill (real name + color via the app's canonical
          // StagePill), matching the deal-stage rows in Storybook.
          item.summary = (
            <>
              changed sales stage to{' '}
              <StagePill
                stageId={d.stageChangeToId}
                className="ml-0.5 align-middle"
              />
            </>
          )
        }
        if (d.email) {
          // A rich HTML email doesn't render well inline in a narrow pane, so the
          // row shows the subject + opens the full message in the (read-only)
          // EmailsModal on click — rather than expanding to a teaser link.
          item.detail = d.email.subject
          item.onClick = () => setSelectedEmail(buildEmailFromEvent(event))
        } else if (d.noteContent) {
          // Note rows expand to the full body — rendered rich (structured JSON,
          // else the read-only Editor for HTML/delta), matching the detail page.
          item.expandedContent = (
            <div className="text-sm text-fbui-text-body">
              <JsonIntoUl
                value={d.noteContent}
                fallback={
                  <Editor
                    value={d.noteContent}
                    readOnly
                    disableToolbar
                    borderless
                  />
                }
              />
            </div>
          )
        } else if (d.documentFile) {
          // Document rows open the file in a new tab on click (matching the
          // detail-page document card); the name is the preview line.
          const fileUrl = d.documentFile
          item.detail = d.detail
          item.onClick = () =>
            window.open(fileUrl, '_blank', 'noopener,noreferrer')
        } else if (d.detail) {
          item.detail = d.detail
        }
        return item
      }),
    [events]
  )

  if (isLoading && events.length === 0) {
    return <ActivitySkeleton />
  }

  if (error) {
    // Muted, non-blocking — this is a side-panel corner, not a page-level
    // failure. A neutral in-place Retry re-runs just this query (no page
    // reload), making the recovery discoverable without closing/reopening.
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-fbui-text-secondary">
          Couldn’t load activity.
        </span>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    )
  }

  return (
    <>
      <ActivityTimeline
        items={items}
        variant="plain"
        size="md"
        // Only the "nothing here" copy when there's genuinely nothing left to
        // fetch — otherwise a page that filters down to zero rows (all
        // email-UPDATE / empty-doc events) would show "no activity" above a
        // "Load more" button.
        emptyState={
          hasNextPage ? undefined : (
            <p className="text-sm text-fbui-text-secondary">
              No recent activity for this company.
            </p>
          )
        }
      />
      {hasNextPage && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
      {selectedEmail && (
        <ScopedEmailsModal
          show={!!selectedEmail}
          onHide={() => setSelectedEmail(null)}
          emailOnlyMode
          initialEmail={selectedEmail}
        />
      )}
    </>
  )
}
